import { ApiError, authedFetch } from '@/lib/api-client'
import type {
  Apariencia,
  Conclusiones,
  FichaStatus,
  Gusto,
  Nariz,
  Role,
} from '@escuela/ficha-domain'

// Wire format de la API (ver /specs/docente-fichas.md "Endpoints API" +
// /specs/api.md). Fichas de referencia (docente/admin): server-side, no sync.
// Regla de autoria: me.rol === 'ADMIN' || ficha.userId === me.id.

export interface FichaAuthor {
  id: string
  nombre: string
  apellido: string
}

/**
 * DTO de una ficha de referencia devuelto por /admin/fichas. Es una FichaCata
 * (contenido evaluable) mas los metadatos de referencia y el autor embebido.
 */
export interface ReferenceFichaDto {
  id: string
  vino: string
  cosecha: string
  origen: string
  alcPorcentaje: string
  apariencia: Apariencia
  nariz: Nariz
  gusto: Gusto
  conclusiones: Conclusiones
  authorRole: Role
  isReference: boolean
  status: FichaStatus
  userId: string
  author: FichaAuthor
  /** Foto del vino (URL absoluta de Cloudinary) o null. */
  imagenUrl: string | null
  publishedAt: string | null
  createdAt: string
  updatedAt: string
}

/** Contenido evaluable enviado al crear/editar (subconjunto de FichaCata). */
export interface FichaContentBody {
  vino: string
  cosecha: string
  origen: string
  alcPorcentaje: string
  apariencia: Apariencia
  nariz: Nariz
  gusto: Gusto
  conclusiones: Conclusiones
}

export interface CreateFichaBody extends FichaContentBody {
  id?: string
}

export type UpdateFichaBody = Partial<FichaContentBody>

export interface ListFichasParams {
  status?: FichaStatus
  /** Filtro por autor (id). Solo ADMIN; el server lo ignora para DOCENTE. */
  author?: string
  q?: string
}

export async function listFichas(
  params: ListFichasParams = {},
): Promise<ReferenceFichaDto[]> {
  const search = new URLSearchParams()
  if (params.status) search.set('status', params.status)
  if (params.author) search.set('author', params.author)
  if (params.q && params.q.trim().length > 0) search.set('q', params.q.trim())
  const query = search.toString()
  const res = await authedFetch<{ items: ReferenceFichaDto[] }>(
    `/admin/fichas${query ? `?${query}` : ''}`,
    { method: 'GET' },
  )
  return res.items
}

export async function getFicha(id: string): Promise<ReferenceFichaDto> {
  return authedFetch<ReferenceFichaDto>(`/admin/fichas/${id}`, { method: 'GET' })
}

export async function createFicha(body: CreateFichaBody): Promise<ReferenceFichaDto> {
  return authedFetch<ReferenceFichaDto>('/admin/fichas', { method: 'POST', body })
}

export async function updateFicha(
  id: string,
  body: UpdateFichaBody,
): Promise<ReferenceFichaDto> {
  return authedFetch<ReferenceFichaDto>(`/admin/fichas/${id}`, {
    method: 'PATCH',
    body,
  })
}

export async function publishFicha(id: string): Promise<ReferenceFichaDto> {
  return authedFetch<ReferenceFichaDto>(`/admin/fichas/${id}/publish`, {
    method: 'POST',
  })
}

export async function unpublishFicha(id: string): Promise<ReferenceFichaDto> {
  return authedFetch<ReferenceFichaDto>(`/admin/fichas/${id}/unpublish`, {
    method: 'POST',
  })
}

export async function deleteFicha(id: string): Promise<void> {
  await authedFetch<void>(`/admin/fichas/${id}`, { method: 'DELETE' })
}

// Foto del vino (multipart). authedFetch fuerza Content-Type JSON, asi que para
// archivos usamos fetch directo con el token de Firebase (mismo patron que
// noticias/api.ts uploadNoticiaImagen).
export async function uploadFichaImagen(id: string, file: File): Promise<ReferenceFichaDto> {
  const { getFirebaseAuth } = await import('@/features/auth/firebase')
  const auth = getFirebaseAuth()
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : null

  const form = new FormData()
  form.append('file', file)

  const headers = new Headers()
  headers.set('Accept', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  let res: Response
  try {
    res = await fetch(`${apiBaseUrl()}/admin/fichas/${id}/imagen`, {
      method: 'POST',
      headers,
      body: form,
    })
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'No pudimos conectarnos al servidor.')
  }

  const text = await res.text()
  const data: unknown = text ? safeJson(text) : null
  if (!res.ok) {
    const err = errShape(data)
    throw new ApiError(res.status, err.code, err.message)
  }
  return data as ReferenceFichaDto
}

export async function deleteFichaImagen(id: string): Promise<ReferenceFichaDto> {
  return authedFetch<ReferenceFichaDto>(`/admin/fichas/${id}/imagen`, { method: 'DELETE' })
}

function apiBaseUrl(): string {
  const envBase = import.meta.env.VITE_API_BASE_URL as string | undefined
  if (!envBase) throw new Error('VITE_API_BASE_URL no está configurada.')
  return envBase.replace(/\/$/, '')
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function errShape(data: unknown): { code: string; message: string } {
  if (data && typeof data === 'object' && 'error' in data) {
    const e = (data as { error?: { code?: string; message?: string } }).error
    if (e) return { code: e.code ?? 'UNKNOWN', message: e.message ?? 'Ocurrió un error.' }
  }
  return { code: 'UNKNOWN', message: 'Ocurrió un error inesperado.' }
}
